import React, { useState } from "react";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Container, Card, Form, Row, Col, Table, Button, Modal, Badge } from "react-bootstrap";
import { Select, DatePicker } from 'antd';
import { FilterOutlined, CheckOutlined, CloseOutlined, EyeOutlined } from '@ant-design/icons';
import FormItem from '@/common/FormItem/FormItem';
import PageTitle from "@/common/PageTitle/PageTitle";
import PaginationRow from "@/components/PaginationRow/PaginationRow";
import styles from "./pending-approvals.module.scss";

interface PendingApproval {
  id: string;
  type: 'music_vendor' | 'installer_vendor' | 'music_vendor_role' | 'installer_vendor_role';
  name: string;
  companyName: string;
  email: string;
  phone: string;
  submittedAt: string;
  status: 'pending' | 'approved' | 'rejected';
  description: string;
  additionalInfo?: string;
}

const PendingApprovals: React.FC = () => {
  // Filter state
  const [filters, setFilters] = useState({
    type: undefined as string | undefined,
    status: undefined as string | undefined,
    submittedDate: undefined as string | undefined,
  });
  // Reset counter for forcing re-render of components
  const [resetCounter, setResetCounter] = useState(0);
  const [page, setPage] = useState(1);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState<PendingApproval | null>(null);

  // Mock data
  const pendingApprovals: PendingApproval[] = [
    {
      id: 'PA-001',
      type: 'music_vendor',
      name: 'John Smith',
      companyName: 'Smith Music Productions',
      email: 'john@smithmusic.com',
      phone: '(555) 123-4567',
      submittedAt: '2024-01-15',
      status: 'pending',
      description: 'Music production company specializing in indie rock',
      additionalInfo: 'Requesting access to upload and manage music content'
    },
    {
      id: 'PA-002',
      type: 'music_vendor',
      name: 'Sarah Johnson',
      companyName: 'Johnson Records',
      email: 'sarah@johnsonrecords.com',
      phone: '(555) 234-5678',
      submittedAt: '2024-01-16',
      status: 'pending',
      description: 'Record label focused on electronic music',
      additionalInfo: 'Looking to distribute electronic music content'
    },
    {
      id: 'PA-003',
      type: 'installer_vendor',
      name: 'Mike Wilson',
      companyName: 'Wilson Audio Solutions',
      email: 'mike@wilsonaudio.com',
      phone: '(555) 345-6789',
      submittedAt: '2024-01-17',
      status: 'pending',
      description: 'Audio installation and technical services',
      additionalInfo: 'Specializes in commercial audio installations'
    },
    {
      id: 'PA-004',
      type: 'music_vendor_role',
      name: 'Lisa Brown',
      companyName: 'Brown Music Group',
      email: 'lisa@brownmusic.com',
      phone: '(555) 456-7890',
      submittedAt: '2024-01-18',
      status: 'pending',
      description: 'Requesting Music Supervisor role',
      additionalInfo: 'Current Music Vendor requesting elevated permissions'
    },
    {
      id: 'PA-005',
      type: 'installer_vendor',
      name: 'David Lee',
      companyName: 'Lee Technical Services',
      email: 'david@leetechnical.com',
      phone: '(555) 567-8901',
      submittedAt: '2024-01-19',
      status: 'pending',
      description: 'Technical installation and maintenance services',
      additionalInfo: 'Focus on KrossKast device installations'
    }
  ];

  // Generate filter options dynamically
  const filterOptions = {
    types: [
      { label: 'Music Vendors', value: 'music_vendor' },
      { label: 'Installer Vendors', value: 'installer_vendor' },
      { label: 'Music Vendor Roles', value: 'music_vendor_role' },
      { label: 'Installer Vendor Roles', value: 'installer_vendor_role' }
    ],
    status: ['Pending', 'Approved', 'Rejected']
  };

  // Filter approvals based on search terms
  const filteredApprovals = pendingApprovals.filter(approval => {
    return (
      (!filters.type || approval.type === filters.type) &&
      (!filters.status || approval.status === filters.status.toLowerCase()) &&
      (!filters.submittedDate || approval.submittedAt === filters.submittedDate)
    );
  });
  
  const totalPages = 1; // Since we only have a few test approvals

  // Filter change
  const handleFilterChange = (key: string, value: string | undefined) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    setFilters({ 
      type: undefined, 
      status: undefined, 
      submittedDate: undefined 
    });
    setResetCounter(prev => prev + 1);
  };

  const handleOnClickNext = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  const handleOnClickPrev = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleViewDetails = (approval: PendingApproval) => {
    setSelectedApproval(approval);
    setShowDetailsModal(true);
  };

  const handleApprove = (approvalId: string) => {
    console.log('Approving:', approvalId);
    // In real app, this would make an API call
  };

  const handleReject = (approvalId: string) => {
    console.log('Rejecting:', approvalId);
    // In real app, this would make an API call
  };

  const getTypeLabel = (type: string) => {
    const typeMap: { [key: string]: string } = {
      'music_vendor': 'Music Vendor',
      'installer_vendor': 'Installer Vendor',
      'music_vendor_role': 'Music Vendor Role',
      'installer_vendor_role': 'Installer Vendor Role'
    };
    return typeMap[type] || type;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge bg="warning">Pending</Badge>;
      case 'approved':
        return <Badge bg="success">Approved</Badge>;
      case 'rejected':
        return <Badge bg="danger">Rejected</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  return (
    <Container fluid>
      <Card>
        <Card.Body>
          <Card.Title style={{ display: 'flex', justifyContent: 'space-between' }}>
            <PageTitle title="Pending Approvals (Vendors)" />
            <Button 
              onClick={handleReset}
              style={{ 
                background: 'white', 
                color: '#333', 
                border: '1px solid #e0e0e0', 
                borderRadius: '6px',
                padding: '8px 16px',
                fontWeight: '500'
              }}
            >
              <FilterOutlined /> Reset All
            </Button>
          </Card.Title>
          
          <Form className={styles.form}>
            <Row>
              <Col>
                <Row className="mb-2">
                  <Col>
                    <Row>
                      <Col md={4}>
                        <FormItem
                          label="Type"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`type-reset-${resetCounter}`}
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any type"
                            value={filters.type}
                            onChange={value => handleFilterChange('type', value)}
                            options={filterOptions.types}
                          />
                        </FormItem>
                      </Col>
                      <Col md={4}>
                        <FormItem
                          label="Status"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`status-reset-${resetCounter}`}
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any status"
                            value={filters.status}
                            onChange={value => handleFilterChange('status', value)}
                            options={filterOptions.status.map(status => ({ label: status, value: status }))}
                          />
                        </FormItem>
                      </Col>
                      <Col md={4}>
                        <FormItem
                          label="Submitted Date"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <DatePicker
                            key={`submittedDate-reset-${resetCounter}`}
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any date"
                            value={filters.submittedDate ? new Date(filters.submittedDate) : undefined}
                            onChange={date => handleFilterChange('submittedDate', date ? date.toISOString().split('T')[0] : undefined)}
                          />
                        </FormItem>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Form>
          
          {/* Pending Approvals Section */}
          <div className={styles.sectionContainer}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>Pending Approvals ({filteredApprovals.length})</h3>
            </div>
            
            {/* Pending Approvals Table */}
            <Table hover responsive className="legacy-row-height-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Name</th>
                  <th>Company</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Submitted</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredApprovals.map((approval) => (
                  <tr key={approval.id}>
                    <td>
                      <Badge bg="info">{getTypeLabel(approval.type)}</Badge>
                    </td>
                    <td>{approval.name}</td>
                    <td>{approval.companyName}</td>
                    <td>{approval.email}</td>
                    <td>{approval.phone}</td>
                    <td>{approval.submittedAt}</td>
                    <td>{getStatusBadge(approval.status)}</td>
                    <td>
                      <div className={styles.actionButtons}>
                        <Button 
                          variant="link" 
                          className={styles.actionLink}
                          onClick={() => handleViewDetails(approval)}
                        >
                          <EyeOutlined /> View
                        </Button>
                        <Button 
                          variant="link" 
                          className={styles.approveLink}
                          onClick={() => handleApprove(approval.id)}
                        >
                          <CheckOutlined /> Approve
                        </Button>
                        <Button 
                          variant="link" 
                          className={styles.rejectLink}
                          onClick={() => handleReject(approval.id)}
                        >
                          <CloseOutlined /> Reject
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          
          <PaginationRow
            page={page}
            onPageChange={(p) => setPage(p)}
            totalPages={typeof totalPages === "number" ? totalPages : undefined}
            onClickPrevious={handleOnClickPrev}
            handleOnClickNext={handleOnClickNext}
            disableLeft={page === 1}
            disableNext={page === totalPages}
          />
        </Card.Body>
      </Card>

      {/* Approval Details Modal */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Approval Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedApproval && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Name:</strong> {selectedApproval.name}
                </Col>
                <Col md={6}>
                  <strong>Company:</strong> {selectedApproval.companyName}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Email:</strong> {selectedApproval.email}
                </Col>
                <Col md={6}>
                  <strong>Phone:</strong> {selectedApproval.phone}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Type:</strong> {getTypeLabel(selectedApproval.type)}
                </Col>
                <Col md={6}>
                  <strong>Submitted:</strong> {selectedApproval.submittedAt}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={12}>
                  <strong>Description:</strong>
                  <p>{selectedApproval.description}</p>
                </Col>
              </Row>
              {selectedApproval.additionalInfo && (
                <Row className="mb-3">
                  <Col md={12}>
                    <strong>Additional Information:</strong>
                    <p>{selectedApproval.additionalInfo}</p>
                  </Col>
                </Row>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
          <Button 
            variant="success" 
            onClick={() => selectedApproval && handleApprove(selectedApproval.id)}
          >
            <CheckOutlined /> Approve
          </Button>
          <Button 
            variant="danger" 
            onClick={() => selectedApproval && handleReject(selectedApproval.id)}
          >
            <CloseOutlined /> Reject
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default withAuthenticationRequired(PendingApprovals);
